import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import { Link, useSearchParams } from "react-router-dom";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import GameText from "components/admin/GameText";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate } from "utils/format";

const PVP_TAB = "__pvp";
const TAB_SX = { flex: "0 0 auto", px: 1.5, whiteSpace: "nowrap" };

function PlayerName({ userId, name }) {
  const canPlayer = useCan(PERMISSIONS.PLAYER_VIEW);
  if (!canPlayer || !userId) return name ? <GameText text={name} /> : "-";
  return (
    <MDTypography component={Link} to={`/players/${userId}`} variant="button" color="info">
      <GameText text={name} />
    </MDTypography>
  );
}

PlayerName.defaultProps = { userId: null, name: "" };
PlayerName.propTypes = { userId: PropTypes.number, name: PropTypes.string };

function PvpTab() {
  const pvp = useFetch(() => api.get("/api/admin/top/pvp", { limit: 200 }), []);
  const data = pvp.data;
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={7}>
        <PageCard
          title={data ? `Xếp hạng PvP mùa ${data.season}` : "Xếp hạng PvP"}
          subtitle={
            data
              ? `Kết thúc ${formatDate(data.seasonEnd)} · điểm khởi đầu ${data.pointStart}`
              : undefined
          }
          color="error"
          noPadding
          actions={
            <MDButton size="small" color="white" onClick={() => pvp.reload()}>
              <Icon>refresh</Icon>
            </MDButton>
          }
        >
          {pvp.error && <Alert severity="error">{pvp.error}</Alert>}
          <AdminTable
            loading={pvp.loading}
            rows={data?.rows || []}
            rowKey="userId"
            emptyText="Mùa này chưa có trận nào"
            columns={[
              { key: "rank", label: "Hạng", width: 60 },
              {
                key: "name",
                label: "Nhân vật",
                render: (r) => (
                  <MDBox display="flex" gap={1} alignItems="center">
                    <PlayerName userId={r.userId} name={r.name} />
                    {r.online && <Chip size="small" color="success" label="Online" />}
                  </MDBox>
                ),
              },
              { key: "point", label: "Điểm", align: "right" },
              { key: "win", label: "Thắng", align: "right" },
              { key: "lose", label: "Thua", align: "right" },
              {
                key: "rate",
                label: "Tỉ lệ thắng",
                align: "right",
                render: (r) =>
                  r.win + r.lose > 0 ? `${Math.round((r.win * 100) / (r.win + r.lose))}%` : "-",
              },
            ]}
          />
        </PageCard>
      </Grid>
      <Grid item xs={12} lg={5}>
        <PageCard title="Trận gần đây" color="dark" noPadding>
          <AdminTable
            loading={pvp.loading}
            rows={data?.matches || []}
            rowKey="id"
            emptyText="Chưa có trận"
            columns={[
              {
                key: "match",
                label: "Trận",
                render: (m) => (
                  <MDBox>
                    <PlayerName userId={m.winnerId} name={m.winnerName} /> thắng{" "}
                    <PlayerName userId={m.loserId} name={m.loserName} />
                    <MDTypography variant="caption" color="text" display="block">
                      {m.winnerPoint} / {m.loserPoint} điểm · {m.turns} lượt
                    </MDTypography>
                  </MDBox>
                ),
              },
              { key: "time", label: "Lúc", render: (m) => formatDate(m.time) },
            ]}
          />
        </PageCard>
      </Grid>
    </Grid>
  );
}

function Top() {
  const tops = useFetch(() => api.get("/api/admin/top"), []);
  const [run, busy] = useRunner();
  // Tab ghi lên URL (?tab=) để gửi link thẳng tới một bảng xếp hạng.
  const [params, setParams] = useSearchParams();
  const list = tops.data || [];
  const raw = params.get("tab");
  const tab = raw === PVP_TAB || list.some((t) => String(t.id) === raw) ? raw : null;
  const setTab = (v) => setParams({ tab: String(v) }, { replace: true });
  const currentId =
    tab === PVP_TAB
      ? PVP_TAB
      : list.find((t) => String(t.id) === tab)?.id ?? list[0]?.id ?? PVP_TAB;
  const current = list.find((t) => t.id === currentId);

  const refresh = async () => {
    const { ok, result } = await run(
      () => api.post("/api/admin/top/refresh", { id: current.id }),
      "Đã làm mới bảng xếp hạng"
    );
    if (ok) tops.setData(list.map((t) => (t.id === result.id ? result : t)));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="top" />
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={currentId}
            onChange={(e, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {list.map((t) => (
              <Tab key={t.id} value={t.id} label={t.name} sx={TAB_SX} />
            ))}
            <Tab value={PVP_TAB} label="PvP theo mùa" sx={TAB_SX} />
          </Tabs>
        </Card>
        {tops.error && <Alert severity="error">{tops.error}</Alert>}
        {currentId === PVP_TAB && <PvpTab />}
        {current && (
          <PageCard
            title={current.name}
            subtitle={`${current.desc || ""} · Máy chủ tự làm mới mỗi 15 phút`}
            color="info"
            noPadding
            actions={
              <MDButton size="small" color="white" disabled={busy} onClick={refresh}>
                <Icon>refresh</Icon>&nbsp;Làm mới ngay
              </MDButton>
            }
          >
            <AdminTable
              loading={tops.loading}
              rows={current.rows}
              rowKey={(r) => `${r.rank}-${r.userId}`}
              emptyText="Bảng trống"
              columns={[
                { key: "rank", label: "Hạng", width: 60 },
                {
                  key: "name",
                  label: "Tên",
                  render: (r) => (
                    <PlayerName
                      userId={current.idIsUser ? r.userId : null}
                      name={r.title || r.name}
                    />
                  ),
                },
                {
                  key: "desc",
                  label: "Thông tin",
                  wrap: true,
                  render: (r) => <GameText text={r.desc} />,
                },
              ]}
            />
          </PageCard>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Top;
